import React, { useEffect, useMemo, useState } from "react";
import { pageLink } from "@/lib/landerDomain";
import { DomainCheckButton } from "@/components/domaincheck/DomainCheckButton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PixelBadge } from "@/components/pixels/PixelBadge";
import { loadPixelBadges } from "@/lib/usePixelBadges";
import { HIGHLIGHT, focusFixTarget } from "@/components/pixels/pixelFix";
import { toast } from "sonner";
import { FindingFix } from "@/components/readiness/FindingFix";
import { goToField } from "@/components/readiness/readinessFixes";
import {
  ArrowLeft, FloppyDisk, Upload, Image as ImageIcon, Trash, Play, Pause,
  Eye, LinkSimple, Plus, X, MapPin, Target, Sparkle,
  CheckCircle, WarningCircle, Info, XCircle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TCPA_LANGUAGE } from "@/pages/landing/config";
import { VERTICAL_PRESETS, VERTICAL_ORDER, verticalLabel } from "@/lib/verticalPresets";
import { VERTICAL_SEO_PRESETS, defaultSeoTitle } from "@/lib/verticalSeoPresets";
import NewDestinationModal from "@/components/delivery/NewDestinationModal";
import HelpLink from "@/components/HelpLink";
import ResponsiveSectionAccordion from "@/components/mobile/ResponsiveSectionAccordion";
import MobileStickyActions from "@/components/mobile/MobileStickyActions";
import { GlobalFunnelNotice } from "@/components/GlobalFunnelNotice";
import { PhoneDisplaySelect, phoneModeOf } from "@/lib/phoneDisplay";
import { EMPTY_NOTIFY, NotifyBuyerBox, notifyParams } from "@/components/NotifyBuyerBox";

/**
 * Iter-43: Full-page CRUD editor for a custom buyer landing page.
 *
 * Route wiring:
 *   /admin/landing-pages/custom/new     → mode="create"
 *   /admin/landing-pages/custom/:id     → mode="edit"
 *
 * Sections (top → bottom):
 *   1. Header (buyer destination + firm name + phone + slug + status)
 *   2. Hero (kicker + headline + accent + subheadline + urgency + image picker)
 *   3. Stat tiles (up to 3)
 *   4. Content sections (What It Is / Signs & Symptoms / Take Action / etc.)
 *   5. In the news (title + URL rows)
 *   6. CTA + TCPA + state restrictions
 *
 * Image picker offers:
 *   x. Upload your own (multipart to /api/admin/custom-landing-pages/upload-image)
 *   y. Pick from the curated stock library (12 PI-appropriate images)
 *   z. Paste any URL directly
 */
const DEFAULT_SECTIONS = [
  { title: "WHAT IT IS",
    body: "People get injured every day — in car crashes, slip and falls, workplace accidents, and defective product incidents. Most people simply go to the hospital and pay out of pocket. Depending on the negligence involved, you may be entitled to substantial compensation. Billions of dollars are recovered every year by injured victims who consult an attorney." },
  { title: "SIGNS & SYMPTOMS",
    body: "Effects vary from injury to injury. Beyond obvious physical harm — sprains, broken bones, head or spine injuries, internal bleeding — you may also be dealing with mental distress or trauma. Because it's hard to know what constitutes a case, it's best to speak with a personal-injury attorney to assess whether pursuing a claim makes sense for your situation." },
  { title: "TAKE ACTION",
    body: "If you or a loved one has suffered a personal injury, don't stay silent. Whether the injury came from a vehicle collision, a work accident, medical care, or a defective product, you may be entitled to major compensation. Let us connect you with an attorney who will fight for the recovery you deserve — free case review, no fee unless we win." },
];

const DEFAULT_STATS = [
  { number: "24hr", label: "Attorney response guaranteed" },
  { number: "No fee", label: "Unless we recover for you" },
  { number: "100%", label: "Confidential" },
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

// US Census regions — used for one-click multi-state selection in the
// state-allowlist grid so an admin building a Southeast-focused firm's
// page can tick every relevant state at once.
const US_REGIONS = {
  Northeast: ["CT", "ME", "MA", "NH", "NJ", "NY", "PA", "RI", "VT"],
  Midwest:   ["IL", "IN", "IA", "KS", "MI", "MN", "MO", "NE", "ND", "OH", "SD", "WI"],
  South:     ["AL", "AR", "DE", "DC", "FL", "GA", "KY", "LA", "MD", "MS", "NC", "OK", "SC", "TN", "TX", "VA", "WV"],
  West:      ["AK", "AZ", "CA", "CO", "HI", "ID", "MT", "NV", "NM", "OR", "UT", "WA", "WY"],
};

export default function AdminCustomLandingPageEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const isMediaBuyer = user?.role === "media_buyer";
  // Iter-45.5: when creating a NEW LP, admins pick a vertical first.
  // `?vertical=auto` in the URL skips the picker and pre-fills. Editing
  // an existing LP always uses the stored vertical.
  const preselectVertical = searchParams.get("vertical");
  // Iter-62.2.1: preselect form_experience from URL query when creating a
  // new landing page. Standard / Quiz / Focused Funnel cards in the
  // Landing Pages admin link into this editor with the right experience
  // pre-filled. Ignored on Edit (existing value wins).
  const preselectExperience = (() => {
    const raw = (searchParams.get("experience") || "").toLowerCase();
    return ["standard", "quiz", "focused_funnel"].includes(raw) ? raw : null;
  })();
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const [busyImage, setBusyImage] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [showAiSeo, setShowAiSeo] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [stockImages, setStockImages] = useState([]);
  const [showStockPicker, setShowStockPicker] = useState(false);
  const [globalPixels, setGlobalPixels] = useState({});
  // Iter-54: inline destination creation. `newDestSlot` remembers whether
  // the freshly-saved destination should land in `buyer_destination_id`
  // (primary) or one of the two CC slots (0 or 1).
  const [showNewDest, setShowNewDest] = useState(false);
  const [newDestSlot, setNewDestSlot] = useState("primary");
  const [testingDestId, setTestingDestId] = useState(null);
  const [showVerticalPicker, setShowVerticalPicker] = useState(
    !isEdit && !preselectVertical && !isMediaBuyer,
  );
  // Iter-51: launch-readiness report for THIS page. Loaded once the page
  // is saved (needs an id to exist); refetched on every successful save
  // so the panel reflects the latest state.
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  const [form, setForm] = useState(() => {
    const preset = preselectVertical && VERTICAL_PRESETS[preselectVertical]
      ? VERTICAL_PRESETS[preselectVertical]
      : VERTICAL_PRESETS.other;
    const vertical = (preselectVertical && VERTICAL_PRESETS[preselectVertical])
      ? preselectVertical
      : "other";
    return {
      slug: "",
      buyer_destination_id: "",
      cc_destination_ids: [],
      fanout_when_primary_inactive: false,
      firm_name: "",
      vertical,
      phone: "",
      // Iter-53: per-page phone-display hide switch. False by default so
      // the page renders the phone CTAs exactly like every existing page.
      hide_phone_display: false,
      brand_color: "#111111",
      hero_kicker: preset.kicker,
      hero_headline: preset.headline,
      hero_headline_accent: preset.headlineAccent,
      hero_subheadline: preset.subheadline,
      hero_image_url: preset.heroImage,
      urgency_badge: preset.urgencyBadge,
      stats: DEFAULT_STATS,
      content_sections: DEFAULT_SECTIONS,
      in_the_news: [],
      cta_text: preset.ctaText,
      tcpa_language: TCPA_LANGUAGE,
      state_allowlist: [],
      thankyou_headline: "",
      thankyou_message: "",
      thankyou_channels: ["call", "text", "email"],
      pixel_overrides: { use_global_defaults: true },
      seo_title: "",
      seo_description: "",
      seo_keywords: "",
      og_image: "",
      status: "draft",
      // Iter-62.2.1: pre-fill from ?experience=... when the admin came
      // from one of the Landing Pages "Experience" cards.
      form_experience: preselectExperience || "standard",
    };
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fixPixels = searchParams.get("fix") === "pixels";
  const fixField = searchParams.get("field");
  useEffect(() => {
    if (!fixPixels || !form.id) return;
    focusFixTarget(...(fixField ? [`[data-testid="custom-lp-pixel-${fixField}"]`] : []), '[data-testid="custom-lp-pixels-section"]');
  }, [fixPixels, fixField, form.id]);

  // Iter-51: refresh the launch-readiness panel for this page.
  const loadReadiness = React.useCallback(async () => {
    if (!isEdit || !id) return;
    setReadinessLoading(true);
    try {
      const r = await api.get(`/admin/custom-landing-pages/${id}/readiness`);
      setReadiness(r.data);
    } catch (err) {
      // Non-fatal — panel just stays empty on failure.
      console.warn("readiness load failed:", err.response?.status);
    } finally {
      setReadinessLoading(false);
    }
  }, [id, isEdit]);

  // Load reference data + the page being edited.
  useEffect(() => {
    if (!isMediaBuyer) {
      api.get("/delivery/destinations")
        .then((r) => setDestinations(r.data?.destinations || r.data || []))
        .catch(() => setDestinations([]));
    }
    api.get("/admin/custom-landing-pages/stock-images")
      .then((r) => setStockImages(r.data?.images || []))
      .catch(() => setStockImages([]));
    // Iter-45: fetch global pixel defaults so we can render "using global"
    // hints in the per-LP override section.
    api.get("/admin/pixels")
      .then((r) => setGlobalPixels(r.data?.config || {}))
      .catch(() => setGlobalPixels({}));
    if (isEdit) {
      api.get(`/admin/custom-landing-pages/${id}`)
        .then((r) => setForm((f) => ({
          ...f,
          ...r.data,
          pixel_overrides: r.data.pixel_overrides || { use_global_defaults: true },
        })))
        .catch((e) => {
          toast.error(e.response?.data?.detail || "Page not found");
          nav("/admin/landing-pages");
        });
      // Iter-51: kick off the readiness fetch in parallel — it doesn't
      // depend on the form load and doesn't block editing.
      loadReadiness();
    }
  }, [id, isEdit, nav, loadReadiness, isMediaBuyer]);

  const destOptions = useMemo(
    () => (destinations || []).filter((d) => d.is_active),
    [destinations],
  );

  const save = async (publishAfter = false, extra = null) => {
    // Iter-45: media buyers can only touch pixel_overrides — save just that.
    if (isMediaBuyer) {
      setSaving(true);
      try {
        await api.patch(
          `/admin/custom-landing-pages/${id}/pixels`,
          form.pixel_overrides || { use_global_defaults: true },
        );
        toast.success("Pixel overrides saved");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Save failed");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!form.is_global_funnel && !form.buyer_lander_of && !(form.firm_name || "").trim()) return toast.error("Firm name is required");
    if (!form.hero_headline.trim()) return toast.error("Hero headline is required");
    if (!form.is_global_funnel && !form.buyer_lander_of && !form.buyer_destination_id) {
      goToField("custom-lp-destination");
      return toast.error("Pick the buyer destination");
    }
    if (form.is_global_funnel && form.status === "published" && !window.confirm(
      "This shared funnel is live. Saving updates the page for every buyer immediately. Continue?",
    )) return;
    setSaving(true);
    try {
      const payload = { ...form, ...(extra || {}) };
      let saved;
      if (isEdit) {
        const r = await api.patch(`/admin/custom-landing-pages/${id}`, payload, notifyParams(notify));
        saved = r.data;
        setNotify(EMPTY_NOTIFY);
      } else {
        const r = await api.post("/admin/custom-landing-pages", payload);
        saved = r.data;
      }
      toast.success(`Saved · /lp/${saved.slug}`);
      loadPixelBadges(true);
      if (publishAfter && saved.status !== "published") {
        try {
          await api.post(`/admin/custom-landing-pages/${saved.id}/publish`, {});
          toast.success("Published — page is now live");
          loadPixelBadges(true);
        } catch (pubErr) {
          // Iter-51: 409 = launch-readiness blockers. Offer super_admin
          // override with a documented reason; otherwise surface the
          // blocker list so the admin can go fix them.
          const detail = pubErr.response?.data?.detail;
          if (pubErr.response?.status === 409
              && typeof detail === "object"
              && detail?.code === "launch_readiness_blocked") {
            const blockerList = (detail.blockers || [])
              .map((b, i) => `${i + 1}. ${b.message}`).join("\n");
            const isSuper = user?.role === "super_admin";
            if (!isSuper) {
              toast.error(
                `Can't publish — ${detail.blockers.length} blocker${
                  detail.blockers.length === 1 ? "" : "s"
                }. Ask a super_admin to override, or fix:\n\n${blockerList}`,
                { duration: 12000 },
              );
              // Refresh so any freshly-saved fields render.
              const rf = await api.get(`/admin/custom-landing-pages/${saved.id}`);
              setForm((f) => ({ ...f, ...rf.data }));
              return;
            }
            const reason = window.prompt(
              `Launch readiness blockers detected:\n\n${blockerList}\n\n` +
              "Enter a reason to override and publish anyway (audited):",
              "",
            );
            if (!reason || !reason.trim()) {
              toast.error("Override cancelled — page stays in current state.");
              return;
            }
            await api.post(
              `/admin/custom-landing-pages/${saved.id}/publish`,
              { override_reason: reason.trim() },
            );
            toast.success("Published with override — see Audit Log.");
            loadPixelBadges(true);
            saved = { ...saved, status: "published" };
          } else {
            throw pubErr;  // rethrow non-blocker errors
          }
        }
      }
      if (!isEdit) nav(`/admin/landing-pages/custom/${saved.id}`);
      else {
        setForm((f) => ({ ...f, ...saved, status: publishAfter ? "published" : saved.status }));
        // Iter-51: refresh readiness after a successful save.
        loadReadiness();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // One-click readiness fix: saves only the changed setting, so other missing fields never block it.
  const quickFix = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/custom-landing-pages/${id}`, patch);
      setForm((f) => ({ ...f, ...patch, updated_at: data.updated_at }));
      toast.success("Fixed — re-checking");
      loadPixelBadges(true);
      loadReadiness();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that fix");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setBusyImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/custom-landing-pages/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Prefix the returned relative URL with the backend origin so the img
      // resolves regardless of where the frontend is rendered.
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      set("hero_image_url", `${base}${r.data.url}`);
      toast.success("Hero image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusyImage(false);
    }
  };

  const pauseOrResume = async () => {
    if (!isEdit) return;
    const nextStatus = form.status === "published" ? "paused" : "published";
    if (form.is_global_funnel && nextStatus === "paused" && !window.confirm(
      `Pause the shared /lp/${form.slug} funnel?\n\nThis takes the page offline for EVERY buyer and affiliate link that uses it.`,
    )) return;
    try {
      const path = nextStatus === "paused"
        ? `/admin/custom-landing-pages/${id}/pause`
        : `/admin/custom-landing-pages/${id}/publish`;
      await api.post(path);
      set("status", nextStatus);
      toast.success(nextStatus === "paused" ? "Paused" : "Published");
      loadPixelBadges(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status update failed");
    }
  };

  const softDelete = async () => {
    if (!isEdit) return;
    // Iter-75.9 — extra guardrail for demo / sample rows. When the row
    // is a sample landing page (Copy sample URLs / Send buyer pitch
    // seed), warn loud so a routine cleanup pass cannot accidentally
    // wipe the pitch surface. The user can restore via the
    // "Restore samples" button on /admin/landing-pages, but the toast
    // trail is easier to avoid than to fix.
    const slug = (form.slug || "").toLowerCase();
    const isSample = Boolean(form.is_sample_page)
      || slug.startsWith("sample-");
    if (isSample) {
      const ok = window.confirm(
        "⚠ This is a DEMO / sample landing page.\n\n"
        + "Deleting it will remove it from the 'Copy sample URLs' and "
        + "'Send buyer pitch' buttons on /admin/landing-pages. Real "
        + "leads are never routed to sample pages, so there is no "
        + "customer impact — but you WILL lose it from your pitch deck "
        + "until you click 'Restore samples'.\n\n"
        + "Continue?"
      );
      if (!ok) return;
    } else if (!window.confirm(
      "Delete this custom landing page? Existing leads keep the audit "
      + "trail; the page becomes unreachable. You can restore it from "
      + "Deleted pages on the Landing pages screen."
    )) {
      return;
    }
    let delNotify;
    if (form.buyer_lander_of && window.confirm("Email the buyer that this page was removed?\n\nOK = email them, Cancel = don't email.")) {
      const note = window.prompt("Note for the buyer (optional). Cancel = don't email:", "");
      if (note !== null) delNotify = notifyParams({ on: true, note });
    }
    try {
      await api.delete(`/admin/custom-landing-pages/${id}`, delNotify);
      toast.success(isSample
        ? "Deleted — restore anytime via 'Restore samples' on /admin/landing-pages"
        : "Deleted");
      nav("/admin/landing-pages");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-2 max-w-6xl mx-auto" data-testid="custom-lp-editor">
      {showVerticalPicker && (
        <VerticalPickerModal
          onPick={(key) => {
            const preset = VERTICAL_PRESETS[key];
            setForm((f) => ({
              ...f,
              vertical: key,
              hero_kicker: preset.kicker,
              hero_headline: preset.headline,
              hero_headline_accent: preset.headlineAccent,
              hero_subheadline: preset.subheadline,
              hero_image_url: preset.heroImage,
              urgency_badge: preset.urgencyBadge,
              cta_text: preset.ctaText,
            }));
            setShowVerticalPicker(false);
          }}
          onCancel={() => nav("/admin/landing-pages")}
        />
      )}
      <button
        onClick={() => nav("/admin/landing-pages")}
        className="label-mono flex items-center gap-1 mb-3 hover:underline"
        data-testid="custom-lp-back"
      >
        <ArrowLeft size={12} /> Back to landing pages
      </button>

      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-mono">
            {isEdit ? "Edit" : "New"} · {form.is_global_funnel ? "shared global funnel" : "custom buyer landing page"}
          </div>
          <h1 className="font-chivo font-black text-4xl tracking-tighter uppercase">
            {form.is_global_funnel ? (form.name || form.slug) : (form.firm_name || "Untitled page")}
          </h1>
          {form.slug && (
            <div className="mt-2 flex items-center gap-2 text-[11px] font-mono">
              <LinkSimple size={11} />
              <a href={pageLink(`/lp/${form.slug}${form.status !== "published" ? "?preview=1" : ""}`, form.status === "published")}
                 target="_blank" rel="noreferrer"
                 className="underline"
                 data-testid="custom-lp-open-preview">
                /lp/{form.slug}
              </a>
              <span className={
                "text-[9px] uppercase tracking-widest px-1.5 py-0.5 border " +
                (form.status === "published"
                  ? "bg-emerald-700 border-emerald-700 text-white"
                  : form.status === "paused"
                    ? "bg-red-600 border-red-600 text-white"
                    : "bg-amber-100 border-amber-700 text-amber-900")
              } data-testid="custom-lp-status-pill">
                {form.status}
              </span>
              {isEdit && (
                <PixelBadge pageId={id} testId="custom-lp-pixel-badge" onFixed={(r) => {
                  if (r.where === "page" && r.page_id === id) set("pixel_overrides", r.pixel_overrides);
                  if (r.where === "main") setGlobalPixels(r.config);
                }} />
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isEdit && !isMediaBuyer && (
            <>
              <button
                onClick={pauseOrResume}
                data-testid="custom-lp-toggle-status"
                className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
              >
                {form.status === "published" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Publish</>}
              </button>
              {!form.is_global_funnel && ["super_admin", "admin"].includes(user?.role) && (
              <button
                onClick={softDelete}
                data-testid="custom-lp-delete"
                className="border border-red-700 text-red-700 px-3 py-2 text-xs uppercase tracking-widest hover:bg-red-700 hover:text-white inline-flex items-center gap-1"
              >
                <Trash size={12} /> Delete
              </button>
              )}
            </>
          )}
          <button
            onClick={() => save(false)}
            disabled={saving}
            data-testid="custom-lp-save"
            className="border border-black bg-black text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-1"
          >
            <FloppyDisk size={12} weight="bold" />{" "}
            {saving ? "Saving…" : (isMediaBuyer ? "Save pixel overrides"
              : (form.is_global_funnel && form.status === "published" ? "Save (live)" : "Save draft"))}
          </button>
          {!isMediaBuyer && (
          <button
            onClick={() => save(true)}
            disabled={saving}
            data-testid="custom-lp-save-publish"
            className="border-2 border-emerald-700 bg-emerald-50 text-emerald-900 px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-emerald-700 hover:text-white inline-flex items-center gap-1"
          >
            <Play size={12} weight="bold" /> {saving ? "…" : "Save & Publish"}
          </button>
          )}
        </div>
      </div>

      {isEdit && <GlobalFunnelNotice form={form} />}

      {/* Iter-51: launch-readiness panel — only meaningful once the page
          has been saved (needs an id). Sticks under the header so it's
          immediately visible without scrolling. */}
      {isEdit && !form.is_global_funnel && (
        <LaunchReadinessPanel
          readiness={readiness}
          loading={readinessLoading}
          onRefresh={loadReadiness}
          isPublished={form.status === "published"}
          saving={saving}
          onSave={() => save(false)}
          onQuickFix={quickFix}
          pageId={id}
          isBuyerPage={Boolean(form.buyer_lander_of || form.buyer_destination_id)}
        />
      )}

      {/* Iter-52: Form Experience — Standard vs Quiz + A/B experiment.
          Defaults keep every existing page rendering as Standard. Media
          buyers see this section READ-ONLY (they cannot switch it). */}
      {!isMediaBuyer && (
      <div className="brut-card mb-6" data-testid="form-experience-section">
        <div className="border-b border-black px-4 py-3 flex items-center gap-2">
          <div className="font-chivo font-black uppercase tracking-tighter">
            Form Experience
          </div>
          <div className="label-mono">
            Choose how consumers interact with the intake form
          </div>
          <div className="ml-auto flex gap-2">
            <HelpLink slug="admin-experience-types" label="Experiences" testId="help-link-experience" />
            <HelpLink slug="admin-ab-testing" label="A/B" testId="help-link-ab" />
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label
              className={"border-2 p-3 cursor-pointer " + (
                (form.form_experience || "standard") === "standard"
                  ? "border-black bg-black text-white"
                  : "border-zinc-400 bg-white hover:border-black"
              )}
              data-testid="form-experience-standard"
            >
              <input
                type="radio" name="form_experience" value="standard"
                checked={(form.form_experience || "standard") === "standard"}
                onChange={() => set("form_experience", "standard")}
                className="mr-2"
              />
              <span className="font-bold uppercase tracking-widest text-sm">
                Standard Form
              </span>
              <div className="text-[11px] mt-1 opacity-90">
                Display the existing qualification form.
              </div>
            </label>
            <label
              className={"border-2 p-3 cursor-pointer " + (
                form.form_experience === "quiz"
                  ? "border-black bg-black text-white"
                  : "border-zinc-400 bg-white hover:border-black"
              )}
              data-testid="form-experience-quiz"
            >
              <input
                type="radio" name="form_experience" value="quiz"
                checked={form.form_experience === "quiz"}
                onChange={() => set("form_experience", "quiz")}
                className="mr-2"
              />
              <span className="font-bold uppercase tracking-widest text-sm">
                Quiz / One Question at a Time
              </span>
              <div className="text-[11px] mt-1 opacity-90">
                Ask qualification questions one screen at a time with a progress indicator.
              </div>
            </label>
            {/* Iter-57: Focused Funnel radio — additive third experience. */}
            <label
              className={"border-2 p-3 cursor-pointer " + (
                form.form_experience === "focused_funnel"
                  ? "border-black bg-black text-white"
                  : "border-zinc-400 bg-white hover:border-black"
              )}
              data-testid="form-experience-focused-funnel"
            >
              <input
                type="radio" name="form_experience" value="focused_funnel"
                checked={form.form_experience === "focused_funnel"}
                onChange={() => set("form_experience", "focused_funnel")}
                className="mr-2"
              />
              <span className="font-bold uppercase tracking-widest text-sm">
                Focused Funnel
              </span>
              <div className="text-[11px] mt-1 opacity-90">
                Mobile-first icon-based accident selection with one-question-at-a-time screening.
              </div>
            </label>
          </div>

          {/* Iter-57: Focused Funnel-only knobs. Additive block — Standard
              and Quiz pages never render it. Every option maps 1:1 onto
              `focused_funnel_config`. Defaults defined in
              `focusedFunnelConfig.js` mirror the safest per-spec choices. */}
          {form.form_experience === "focused_funnel" && (
            <div
              className="border border-black bg-sky-50 p-3 space-y-3"
              data-testid="focused-funnel-config-panel"
            >
              <div className="label-mono">Focused Funnel configuration</div>
              <div className="flex justify-end -mt-1"><HelpLink slug="admin-focused-funnel" label="Focused Funnel Guide" testId="help-link-focused-funnel" /></div>

              {/* Iter-58 — question visibility vs disqualification rules
                  live in TWO distinct sub-panels so admins never confuse
                  the two responsibilities. Hidden questions skip the
                  funnel entirely; disqualifier toggles only fire on
                  answered questions. */}
              <div className="border border-slate-400 bg-white p-2 space-y-2"
                   data-testid="focused-funnel-visibility-panel">
                <div className="text-[11px] font-bold uppercase tracking-widest">
                  Question visibility
                </div>
                <div className="text-[10px] text-slate-600">
                  Controls whether the visitor is asked the question. Hidden
                  questions leave the corresponding lead field null.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={(form.focused_funnel_config || {}).show_injury_question !== false}
                      onChange={(e) => set("focused_funnel_config",
                        { ...(form.focused_funnel_config || {}), show_injury_question: e.target.checked })}
                      data-testid="ff-show-injury"
                    />
                    <span>Ask physical injury question</span>
                  </label>
                  <label className="flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={(form.focused_funnel_config || {}).show_treatment_question !== false}
                      onChange={(e) => set("focused_funnel_config",
                        { ...(form.focused_funnel_config || {}), show_treatment_question: e.target.checked })}
                      data-testid="ff-show-treatment"
                    />
                    <span>Ask medical treatment question</span>
                  </label>
                  <label className="flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={(form.focused_funnel_config || {}).show_attorney_question !== false}
                      onChange={(e) => set("focused_funnel_config",
                        { ...(form.focused_funnel_config || {}), show_attorney_question: e.target.checked })}
                      data-testid="ff-show-attorney"
                    />
                    <span>Ask attorney representation question</span>
                  </label>
                </div>
              </div>

              <div className="border border-slate-400 bg-white p-2 space-y-2"
                   data-testid="focused-funnel-disqualify-panel">
                <div className="text-[11px] font-bold uppercase tracking-widest">
                  Disqualification rules
                </div>
                <div className="text-[10px] text-slate-600">
                  Controls what happens when the visitor answers. Rules
                  are inactive when the corresponding question is hidden;
                  your setting is preserved for when you re-enable it.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    ["require_injury",       "show_injury_question",    "Disqualify if no physical injury",    "ff-require-injury"],
                    ["require_treatment",    "show_treatment_question", "Disqualify if no medical treatment",  "ff-require-treatment"],
                    ["require_no_attorney",  "show_attorney_question",  "Disqualify if already represented",   "ff-require-no-attorney"],
                  ].map(([rk, sk, label, tid]) => {
                    const shown = (form.focused_funnel_config || {})[sk] !== false;
                    const value = (form.focused_funnel_config || {})[rk] !== false;
                    return (
                      <div key={rk} className="flex flex-col gap-1">
                        <label className={"flex items-center gap-2 text-[12px] " + (shown ? "" : "opacity-50")}>
                          <input
                            type="checkbox"
                            disabled={!shown}
                            checked={value}
                            onChange={(e) => set("focused_funnel_config",
                              { ...(form.focused_funnel_config || {}), [rk]: e.target.checked })}
                            data-testid={tid}
                          />
                          <span>{label}</span>
                        </label>
                        {!shown && (
                          <div className="text-[10px] text-amber-800 pl-6"
                               data-testid={`${tid}-inactive-note`}>
                            This rule only applies when the question is shown.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="label-mono">Consent presentation</label>
                <select
                  className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                  value={(form.focused_funnel_config || {}).consent_presentation || "affirmative_checkbox"}
                  onChange={(e) => set("focused_funnel_config",
                    { ...(form.focused_funnel_config || {}), consent_presentation: e.target.value })}
                  data-testid="ff-consent-presentation"
                >
                  <option value="affirmative_checkbox">Affirmative Checkbox (default)</option>
                  <option value="disclosure_below_form">Disclosure Below Form</option>
                </select>
                <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-500 mt-2 p-2">
                  Compliance configuration — changing this affects ONLY this
                  Focused Funnel page. Existing Standard and Quiz pages continue
                  to use their current consent flow. TrustedForm capture and
                  the underlying TCPA language remain independently configurable.
                </div>
              </div>

              <label className="flex items-center gap-2 text-[12px]">
                <input
                  type="checkbox"
                  checked={(form.focused_funnel_config || {}).require_exact_date_after_qualification === true}
                  onChange={(e) => set("focused_funnel_config",
                    { ...(form.focused_funnel_config || {}), require_exact_date_after_qualification: e.target.checked })}
                  data-testid="ff-require-exact-date"
                />
                Ask for approximate accident date after qualification (optional)
              </label>
            </div>
          )}

          {/* Quiz-only knobs */}
          {(form.form_experience === "quiz" || form.experiment_enabled) && (
            <div className="border border-black bg-amber-50 p-3 space-y-3" data-testid="quiz-config-panel">
              <div className="label-mono">Quiz configuration</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-mono">Intro headline</label>
                  <input
                    type="text" className="w-full border border-black px-3 py-2 mt-1 text-sm"
                    value={(form.quiz_config || {}).intro_headline || ""}
                    onChange={(e) => set("quiz_config",
                      { ...(form.quiz_config || {}), intro_headline: e.target.value })}
                    data-testid="quiz-intro-headline"
                    placeholder="Let's see if you may qualify"
                  />
                </div>
                <div>
                  <label className="label-mono">Intro subheadline</label>
                  <input
                    type="text" className="w-full border border-black px-3 py-2 mt-1 text-sm"
                    value={(form.quiz_config || {}).intro_subheadline || ""}
                    onChange={(e) => set("quiz_config",
                      { ...(form.quiz_config || {}), intro_subheadline: e.target.value })}
                    data-testid="quiz-intro-sub"
                    placeholder="A few quick questions. About one minute."
                  />
                </div>
                <div>
                  <label className="label-mono">Contact-step headline</label>
                  <input
                    type="text" className="w-full border border-black px-3 py-2 mt-1 text-sm"
                    value={(form.quiz_config || {}).contact_headline || ""}
                    onChange={(e) => set("quiz_config",
                      { ...(form.quiz_config || {}), contact_headline: e.target.value })}
                    data-testid="quiz-contact-headline"
                    placeholder="Where should we send your free case review?"
                  />
                </div>
                <div>
                  <label className="label-mono">Progress style</label>
                  <select
                    className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                    value={form.quiz_progress_style || "steps"}
                    onChange={(e) => set("quiz_progress_style", e.target.value)}
                    data-testid="quiz-progress-style"
                  >
                    <option value="steps">Steps (Question 2 of 6)</option>
                    <option value="percent">Percentage (33% complete)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="quiz-layout-panel">
                <div>
                  <label className="label-mono">Quiz layout</label>
                  <select
                    className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                    value={(form.quiz_config || {}).layout || "classic"}
                    onChange={(e) => set("quiz_config", { ...(form.quiz_config || {}), layout: e.target.value })}
                    data-testid="quiz-layout-select"
                  >
                    <option value="classic">Classic — start button, then questions</option>
                    <option value="v2">Version 2 — tap the accident type, app-style questions</option>
                  </select>
                </div>
                {(form.quiz_config || {}).layout === "v2" && (
                  <div>
                    <label className="label-mono">Phone number on Version 2</label>
                    <select
                      className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                      value={(form.quiz_config || {}).v2_phone || "first_page"}
                      onChange={(e) => set("quiz_config", { ...(form.quiz_config || {}), v2_phone: e.target.value })}
                      data-testid="quiz-v2-phone-select"
                    >
                      <option value="first_page">First page only — hidden on question screens</option>
                      <option value="all_screens">First page + small call button on question screens</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={(form.quiz_config || {}).optional_description_enabled !== false}
                    onChange={(e) => set("quiz_config",
                      { ...(form.quiz_config || {}),
                        optional_description_enabled: e.target.checked })}
                    data-testid="quiz-optional-description"
                  />
                  Include optional accident-description question
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={(form.quiz_config || {}).optional_evidence_enabled !== false}
                    onChange={(e) => set("quiz_config",
                      { ...(form.quiz_config || {}),
                        optional_evidence_enabled: e.target.checked })}
                    data-testid="quiz-optional-evidence"
                  />
                  Include optional evidence step
                </label>
              </div>
            </div>
          )}

          {/* A/B experiment — Iter-58 upgraded to 3-way weighted variants
              (Standard / Quiz / Focused Funnel). Backward compatible:
              pages that only used the legacy Standard-vs-Quiz pair keep
              working when `experiment_variants` is absent. */}
          <div className="border border-black bg-zinc-50 p-3" data-testid="experiment-panel">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.experiment_enabled === true}
                onChange={(e) => set("experiment_enabled", e.target.checked)}
                data-testid="experiment-enabled"
              />
              <span className="font-bold uppercase tracking-widest text-sm">
                Enable Experience Testing (A/B/n)
              </span>
            </label>
            {form.experiment_enabled && (() => {
              const legacyStd = form.experiment_standard_weight ?? 50;
              const legacyQuiz = form.experiment_quiz_weight ?? 50;
              const variants = form.experiment_variants || {
                standard: legacyStd, quiz: legacyQuiz,
              };
              const setVariantWeight = (key, val) => {
                const clamped = Math.max(0, Math.min(100, Number(val) || 0));
                set("experiment_variants", { ...variants, [key]: clamped });
                if (key === "standard") set("experiment_standard_weight", clamped);
                if (key === "quiz") set("experiment_quiz_weight", clamped);
              };
              const applyPreset = (label) => {
                let next;
                if (label === "std-quiz") next = { standard: 50, quiz: 50, focused_funnel: 0 };
                if (label === "std-focused") next = { standard: 50, quiz: 0, focused_funnel: 50 };
                if (label === "quiz-focused") next = { standard: 0, quiz: 50, focused_funnel: 50 };
                if (label === "three-way") next = { standard: 34, quiz: 33, focused_funnel: 33 };
                set("experiment_variants", next);
                set("experiment_standard_weight", next.standard);
                set("experiment_quiz_weight", next.quiz);
              };
              const total = Number(variants.standard || 0) + Number(variants.quiz || 0) + Number(variants.focused_funnel || 0);
              const valid = total === 100;
              return (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] font-mono">
                    <div>
                      <div className="uppercase tracking-widest">Standard</div>
                      <input
                        type="number" min={0} max={100}
                        className="w-full border border-black px-3 py-2 mt-1"
                        value={variants.standard ?? 0}
                        onChange={(e) => setVariantWeight("standard", e.target.value)}
                        data-testid="experiment-std-weight"
                      />
                    </div>
                    <div>
                      <div className="uppercase tracking-widest">Quiz</div>
                      <input
                        type="number" min={0} max={100}
                        className="w-full border border-black px-3 py-2 mt-1"
                        value={variants.quiz ?? 0}
                        onChange={(e) => setVariantWeight("quiz", e.target.value)}
                        data-testid="experiment-quiz-weight"
                      />
                    </div>
                    <div>
                      <div className="uppercase tracking-widest">Focused Funnel</div>
                      <input
                        type="number" min={0} max={100}
                        className="w-full border border-black px-3 py-2 mt-1"
                        value={variants.focused_funnel ?? 0}
                        onChange={(e) => setVariantWeight("focused_funnel", e.target.value)}
                        data-testid="experiment-focused-weight"
                      />
                    </div>
                  </div>
                  <div
                    className={"text-[11px] font-mono p-2 " + (
                      valid ? "text-emerald-800 bg-emerald-50 border border-emerald-700"
                            : "text-red-800 bg-red-50 border border-red-700"
                    )}
                    data-testid="experiment-total"
                  >
                    Total: {total}% {valid ? "· OK" : "· MUST EQUAL 100%"}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest">Presets</div>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      ["std-quiz",     "Standard vs Quiz 50/50"],
                      ["std-focused",  "Standard vs Focused 50/50"],
                      ["quiz-focused", "Quiz vs Focused 50/50"],
                      ["three-way",    "Three-Way 34/33/33"],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className="border-2 border-black bg-white px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                        onClick={() => applyPreset(id)}
                        data-testid={`experiment-preset-${id}`}
                      >{label}</button>
                    ))}
                  </div>
                  <div className="text-[10px] text-zinc-600">
                    Sticky assignment: the same anonymous visitor stays on the
                    same variant across refreshes AND across weight changes.
                    New visitors get the latest weights. Preview mode can force
                    any variant.
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      )}

      {/* Section 1 — routing + identity */}
      {!isMediaBuyer && (
      <Section title="Buyer & branding">
        {form.buyer_lander_of && (
          <div className="mb-3 border-2 border-black bg-emerald-50 px-3 py-2 text-xs" data-testid="custom-lp-lander-notice">
            <b>This is {form.firm_name || "a buyer"}&apos;s own page.</b> Leads that fit them go to them first; if they
            can&apos;t take it, the next matching buyer gets it and the visitor sees that buyer&apos;s thank-you page.
            Firm name, phone, logo, legal text and delivery come from the buyer&apos;s settings, so you only edit the page here.
          </div>
        )}
        <Field label="Case vertical" required>
          <select
            value={form.vertical || "other"}
            onChange={(e) => set("vertical", e.target.value)}
            data-testid="custom-lp-vertical-select"
            className="w-full border border-black px-3 py-2 text-sm bg-white"
          >
            {VERTICAL_ORDER.map((k) => (
              <option key={k} value={k}>{VERTICAL_PRESETS[k].label}</option>
            ))}
          </select>
          <div className="text-[10px] font-mono text-zinc-500 mt-1">
            Stamps <code>accident_type</code> on every lead. Change this
            only if the firm is refocusing — hero copy stays as edited.
          </div>
        </Field>
        <Field
          label={form.is_global_funnel ? "Buyer destination (not used for shared funnels)" : form.buyer_lander_of ? "Buyer destination (not used: this page uses its buyer's delivery)" : "Buyer destination (leads route ONLY here)"}
          required={!form.is_global_funnel && !form.buyer_lander_of}
        >
          <div className="flex items-center gap-2">
            <select
              value={form.buyer_destination_id}
              onChange={(e) => set("buyer_destination_id", e.target.value)}
              data-testid="custom-lp-destination"
              className="flex-1 border border-black px-3 py-2 text-sm font-mono"
            >
              <option value="">— Pick a buyer destination —</option>
              {destOptions
                .filter((d) => !(form.cc_destination_ids || []).includes(d.id))
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} · {d.type}
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={() => { setNewDestSlot("primary"); setShowNewDest(true); }}
              data-testid="custom-lp-new-dest-primary"
              className="border-2 border-black bg-white px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
              title="Create a new webhook / email / CRM destination without leaving this page"
            >
              <Plus size={11} weight="bold" /> New
            </button>
            {form.buyer_destination_id && (
              <button
                type="button"
                onClick={async () => {
                  setTestingDestId(form.buyer_destination_id);
                  try {
                    await api.post(`/delivery/destinations/${form.buyer_destination_id}/test`);
                    toast.success("Test lead sent to buyer");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Test send failed");
                  } finally {
                    setTestingDestId(null);
                  }
                }}
                disabled={testingDestId === form.buyer_destination_id}
                data-testid="custom-lp-test-dest-primary"
                className="border border-black bg-white px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white hover:border-emerald-700 disabled:opacity-40 whitespace-nowrap"
                title="Fire a synthetic test lead to this destination"
              >
                {testingDestId === form.buyer_destination_id ? "Sending…" : "Test"}
              </button>
            )}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
            Every form submission delivers here in <b>realtime</b> (sub-second webhook / 
            &lt;60s email / instant CRM push) with a 30s → 2m → 10m → 1h → 6h retry ladder.
            {" "}
            <a
              href="/delivery"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-black font-mono uppercase tracking-widest"
              data-testid="custom-lp-manage-dests"
            >Manage all destinations →</a>
          </div>
        </Field>

        <Field label="Also send to (optional CC — up to 2)">
          <div className="space-y-2">
            {[0, 1].map((slotIdx) => {
              const ccIds = form.cc_destination_ids || [];
              const currentId = ccIds[slotIdx] || "";
              // Iter-54.2: CC options MUST belong to the same buyer as
              // the primary destination. Filter destOptions down to the
              // primary's `client_id` (or `null` if primary is
              // platform-wide) — hides cross-buyer picks in the UI so
              // the admin can't even attempt to save an invalid combo.
              const primaryDest = destOptions.find(
                (d) => d.id === form.buyer_destination_id,
              );
              const primaryClientId = primaryDest
                ? (primaryDest.client_id ?? null)
                : undefined;
              const excluded = new Set([
                form.buyer_destination_id,
                ...ccIds.filter((_, i) => i !== slotIdx),
              ]);
              const options = destOptions.filter((d) => {
                if (excluded.has(d.id)) return false;
                if (primaryClientId === undefined) return false;
                return (d.client_id ?? null) === primaryClientId;
              });
              return (
                <div key={slotIdx} className="flex items-center gap-2">
                  <select
                    value={currentId}
                    onChange={(e) => {
                      const next = [...ccIds];
                      const v = e.target.value;
                      // Replace or clear this slot; compact so empties never
                      // create a gap between slot 0 and slot 1.
                      next[slotIdx] = v;
                      set("cc_destination_ids", next.filter(Boolean));
                    }}
                    data-testid={`custom-lp-cc-dest-${slotIdx}`}
                    className="flex-1 border border-zinc-400 px-3 py-2 text-sm font-mono"
                    disabled={!form.buyer_destination_id}
                  >
                    <option value="">
                      {form.buyer_destination_id
                        ? `— Optional CC destination ${slotIdx + 1} —`
                        : "Pick primary destination first"}
                    </option>
                    {options.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} · {d.type}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setNewDestSlot(`cc-${slotIdx}`); setShowNewDest(true); }}
                    data-testid={`custom-lp-new-dest-cc-${slotIdx}`}
                    className="border border-black bg-white px-2 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
                    disabled={!form.buyer_destination_id}
                    title="Add a new destination and drop it into this slot"
                  >
                    <Plus size={10} weight="bold" /> New
                  </button>
                  {currentId && (
                    <button
                      type="button"
                      onClick={async () => {
                        setTestingDestId(currentId);
                        try {
                          await api.post(`/delivery/destinations/${currentId}/test`);
                          toast.success("Test lead sent to CC destination");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Test send failed");
                        } finally {
                          setTestingDestId(null);
                        }
                      }}
                      disabled={testingDestId === currentId}
                      data-testid={`custom-lp-test-dest-cc-${slotIdx}`}
                      className="border border-black bg-white px-2 py-2 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white hover:border-emerald-700 disabled:opacity-40 whitespace-nowrap"
                    >
                      {testingDestId === currentId ? "Sending…" : "Test"}
                    </button>
                  )}
                </div>
              );
            })}
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              Leads fan out to the primary AND each CC destination — useful for
              &ldquo;push to CRM + notify Slack via Zapier&rdquo; or
              &ldquo;webhook + CC ops email.&rdquo; Each retries independently.
              <br/>
              <span className="font-bold uppercase tracking-widest text-[10px] text-black">
                Buyer isolation:
              </span>{" "}
              CC destinations must belong to the same buyer as the primary —
              only same-buyer options appear in the dropdowns.
            </div>
          </div>
        </Field>

        {/* Iter-54.2 — Primary-pause fanout semantics. Default OFF so a
            paused primary halts ALL production delivery for the page. */}
        <Field label="If the primary destination is paused, keep delivering to CC destinations?">
          <label
            className="flex items-start gap-2 border border-zinc-400 px-3 py-2 mt-1 cursor-pointer hover:bg-amber-50"
            data-testid="custom-lp-fanout-inactive-wrap"
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 border-2 border-black"
              data-testid="custom-lp-fanout-when-primary-inactive"
              checked={!!form.fanout_when_primary_inactive}
              onChange={(e) => set("fanout_when_primary_inactive", e.target.checked)}
            />
            <span className="text-xs text-zinc-700 leading-snug">
              <span className="font-bold uppercase tracking-widest text-[10px] text-black">
                {form.fanout_when_primary_inactive
                  ? "On — primary paused ≠ page paused"
                  : "Off — primary paused halts ALL delivery"}
              </span>
              <br/>
              Off (recommended): a buyer PAUSE stops production delivery for the
              whole page. On: only the failing primary is skipped — active CC
              destinations for the same buyer keep receiving leads. Useful when
              the CC destinations are alternate channels (email + CRM) for the
              same firm.
            </span>
          </label>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Firm name (display)" required>
            <input value={form.firm_name} onChange={(e) => set("firm_name", e.target.value)}
                   data-testid="custom-lp-firm-name"
                   className="w-full border border-black px-3 py-2 text-sm" />
          </Field>
          <Field label="Phone (clickable in top bar)">
            <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
                   placeholder="1-800-555-0199"
                   data-testid="custom-lp-phone"
                   className="w-full border border-black px-3 py-2 text-sm font-mono" />
            {/* Iter-53: per-page hide toggle. Keeps the phone in the DB
                so admins can un-hide with one click once the new
                number is ready. */}
            <label className="mt-2 block text-xs text-zinc-600 leading-snug">
              <span className="font-bold uppercase tracking-widest text-[10px] text-black">Show phone number</span>
              {form.buyer_lander_of ? (
                <span className="mt-1 block border border-dashed border-zinc-400 px-2 py-1.5" data-testid="custom-lp-phone-display-lander">
                  Follows the buyer&apos;s setting. Change it on the buyer&apos;s Thank-you page settings.
                </span>
              ) : (
                <PhoneDisplaySelect value={phoneModeOf(form)} testId="custom-lp-phone-display"
                  onChange={(v) => setForm((f) => ({ ...f, phone_display: v, hide_phone_display: v === "none" }))} />
              )}
              <span className="mt-1 block">
                Landing pages: top bar, hero, footer and sticky mobile call buttons. Thank-you pages: call and text
                buttons.{form.buyer_lander_of ? "" : " Applies to every page this buyer gets leads from."}
              </span>
            </label>
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="URL slug (auto if blank)">
            <div className="flex items-center gap-1">
              <span className="font-mono text-[11px] text-zinc-500">/lp/</span>
              <input value={form.slug || ""} onChange={(e) => set("slug", e.target.value)}
                     placeholder="example-al-injury"
                     data-testid="custom-lp-slug"
                     className="flex-1 border border-black px-3 py-2 text-sm font-mono" />
            </div>
          </Field>
          <Field label="Brand color (hex)">
            <div className="flex items-center gap-2">
              <input type="color" value={form.brand_color} onChange={(e) => set("brand_color", e.target.value)}
                     data-testid="custom-lp-brand-color"
                     className="w-10 h-10 border border-black" />
              <input value={form.brand_color} onChange={(e) => set("brand_color", e.target.value)}
                     className="flex-1 border border-black px-3 py-2 text-sm font-mono" />
            </div>
          </Field>
        </div>
      </Section>
      )}

      {/* Section 2 — hero */}
      {!isMediaBuyer && (
      <Section title="Hero (top of page)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Kicker (small text above headline)">
            <input value={form.hero_kicker || ""} onChange={(e) => set("hero_kicker", e.target.value)}
                   data-testid="custom-lp-hero-kicker"
                   className="w-full border border-black px-3 py-2 text-sm" />
          </Field>
          <Field label="Urgency badge">
            <input value={form.urgency_badge || ""} onChange={(e) => set("urgency_badge", e.target.value)}
                   className="w-full border border-black px-3 py-2 text-sm" />
          </Field>
        </div>
        <Field label="Headline" required>
          <input value={form.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)}
                 placeholder="Injured in a Car Accident?"
                 data-testid="custom-lp-hero-headline"
                 className="w-full border border-black px-3 py-2 text-sm font-bold" />
        </Field>
        <Field label="Headline accent (yellow line beneath)">
          <input value={form.hero_headline_accent || ""} onChange={(e) => set("hero_headline_accent", e.target.value)}
                 placeholder="You may be owed compensation."
                 className="w-full border border-black px-3 py-2 text-sm" />
        </Field>
        <Field label="Subheadline">
          <textarea value={form.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)}
                    rows={3}
                    data-testid="custom-lp-hero-sub"
                    className="w-full border border-black px-3 py-2 text-sm" />
        </Field>

        {/* Hero image picker */}
        <div className="border-2 border-black p-4 bg-zinc-50">
          <div className="label-mono mb-2 flex items-center gap-1">
            <ImageIcon size={12} weight="bold" /> Hero background image
          </div>
          {form.hero_image_url ? (
            <div className="mb-3">
              <img src={form.hero_image_url} alt=""
                   className="w-full h-40 object-cover border border-black"
                   data-testid="custom-lp-hero-preview" />
              <div className="text-[10px] font-mono text-zinc-500 mt-1 break-all">{form.hero_image_url}</div>
            </div>
          ) : (
            <div className="h-24 border border-dashed border-black flex items-center justify-center text-[11px] font-mono text-zinc-500 mb-3">
              No hero image set
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <label className="border border-black px-3 py-2 text-[10px] uppercase tracking-widest cursor-pointer hover:bg-black hover:text-white inline-flex items-center gap-1">
              <Upload size={11} weight="bold" /> Upload
              <input type="file" accept="image/*" className="hidden"
                     data-testid="custom-lp-hero-upload"
                     onChange={(e) => uploadImage(e.target.files?.[0])}
                     disabled={busyImage} />
            </label>
            <button
              type="button"
              onClick={() => setShowStockPicker(!showStockPicker)}
              data-testid="custom-lp-hero-stock"
              className="border border-black px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Pick from stock ({stockImages.length})
            </button>
            <input
              placeholder="or paste an image URL directly"
              value={form.hero_image_url || ""}
              onChange={(e) => set("hero_image_url", e.target.value)}
              className="flex-1 min-w-[240px] border border-black px-3 py-2 text-xs font-mono"
              data-testid="custom-lp-hero-url"
            />
            <button
              type="button"
              onClick={() => setShowAiPanel((v) => !v)}
              data-testid="custom-lp-hero-ai-toggle"
              className={
                "border-2 px-3 py-2 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 " +
                (showAiPanel
                  ? "border-black bg-black text-white"
                  : "border-black bg-amber-100 hover:bg-black hover:text-white")
              }
            >
              <Sparkle size={11} weight="fill" /> AI generate
            </button>
          </div>

          {showAiPanel && (
            <AiHeroPanel
              onGenerated={(url) => set("hero_image_url", url)}
              onClose={() => setShowAiPanel(false)}
            />
          )}


          {showStockPicker && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="custom-lp-stock-grid">
              {stockImages.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { set("hero_image_url", img.url); setShowStockPicker(false); }}
                  data-testid={`custom-lp-stock-${i}`}
                  className="border-2 border-black overflow-hidden hover:ring-4 hover:ring-amber-400 transition text-left"
                >
                  <img src={img.url} alt={img.label} className="w-full h-20 object-cover" loading="lazy" />
                  <div className="p-1 text-[9px] font-mono uppercase tracking-wide bg-zinc-100 border-t border-black">
                    {img.label}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Stat tiles */}
        <div className="mt-4">
          <div className="label-mono mb-2">Hero stat tiles (up to 3)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {(form.stats || []).slice(0, 3).map((s, i) => (
              <div key={i} className="border border-black p-2">
                <input value={s.number || ""}
                       onChange={(e) => set("stats", form.stats.map((x, xi) => xi === i ? { ...x, number: e.target.value } : x))}
                       placeholder="24hr"
                       className="w-full border border-black px-2 py-1 text-sm font-bold mb-1 font-mono" />
                <input value={s.label || ""}
                       onChange={(e) => set("stats", form.stats.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                       placeholder="Attorney response guaranteed"
                       className="w-full border border-black px-2 py-1 text-[11px]" />
              </div>
            ))}
          </div>
        </div>
      </Section>
      )}

      {/* Section 3 — content blocks */}
      {!isMediaBuyer && (
      <Section title="Content sections (like 'WHAT IT IS', 'SIGNS & SYMPTOMS', 'TAKE ACTION')">
        {(form.content_sections || []).map((c, i) => (
          <div key={i} className="border border-black p-3 mb-3 relative"
               data-testid={`custom-lp-section-editor-${i}`}>
            <button
              type="button"
              onClick={() => set("content_sections", form.content_sections.filter((_, xi) => xi !== i))}
              className="absolute top-1 right-1 text-red-700 hover:bg-red-700 hover:text-white p-1"
              data-testid={`custom-lp-section-remove-${i}`}
              title="Remove section"
            ><X size={12} weight="bold" /></button>
            <input value={c.title}
                   onChange={(e) => set("content_sections", form.content_sections.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))}
                   placeholder="Section title"
                   className="w-full border border-black px-2 py-1 text-sm font-bold mb-2 uppercase tracking-widest font-chivo" />
            <textarea value={c.body}
                      onChange={(e) => set("content_sections", form.content_sections.map((x, xi) => xi === i ? { ...x, body: e.target.value } : x))}
                      rows={4}
                      placeholder="Body copy — freeform paragraphs"
                      className="w-full border border-black px-2 py-1 text-sm" />
          </div>
        ))}
        <button
          type="button"
          onClick={() => set("content_sections", [...(form.content_sections || []), { title: "", body: "" }])}
          data-testid="custom-lp-section-add"
          className="border border-black px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
        >
          <Plus size={11} weight="bold" /> Add section
        </button>
      </Section>
      )}

      {/* Section 4 — in the news */}
      {!isMediaBuyer && (
      <Section title="In the news (external links)">
        {(form.in_the_news || []).map((n, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[2fr_3fr_auto] gap-2 mb-2">
            <input value={n.title}
                   onChange={(e) => set("in_the_news", form.in_the_news.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))}
                   placeholder="Article title"
                   className="border border-black px-2 py-1 text-sm" />
            <input value={n.url}
                   onChange={(e) => set("in_the_news", form.in_the_news.map((x, xi) => xi === i ? { ...x, url: e.target.value } : x))}
                   placeholder="https://…"
                   className="border border-black px-2 py-1 text-sm font-mono" />
            <button
              type="button"
              onClick={() => set("in_the_news", form.in_the_news.filter((_, xi) => xi !== i))}
              className="border border-red-700 text-red-700 px-2 py-1 hover:bg-red-700 hover:text-white"
              title="Remove"
            ><X size={12} weight="bold" /></button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => set("in_the_news", [...(form.in_the_news || []), { title: "", url: "" }])}
          className="border border-black px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
        >
          <Plus size={11} weight="bold" /> Add link
        </button>
      </Section>
      )}

      {/* Section 5 — CTA + TCPA + state gate */}
      {!isMediaBuyer && (
      <Section title="Form &amp; compliance">
        <Field label="CTA button text">
          <input value={form.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)}
                 data-testid="custom-lp-cta-text"
                 className="w-full border border-black px-3 py-2 text-sm" />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Privacy Policy URL">
            <input
              type="url"
              value={form.privacy_url || ""}
              onChange={(e) => set("privacy_url", e.target.value)}
              placeholder="https://firm.example.com/privacy"
              data-testid="custom-lp-privacy-url"
              className="w-full border border-black px-3 py-2 text-sm font-mono"
            />
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              Link shown in footer + TCPA consent block. Clears the readiness
              privacy-policy warning.
            </div>
          </Field>
          <Field label="Terms of Service URL">
            <input
              type="url"
              value={form.terms_url || ""}
              onChange={(e) => set("terms_url", e.target.value)}
              placeholder="https://firm.example.com/terms"
              data-testid="custom-lp-terms-url"
              className="w-full border border-black px-3 py-2 text-sm font-mono"
            />
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              Link shown in footer. Clears the readiness terms-of-service
              warning.
            </div>
          </Field>
        </div>
        <Field label="TCPA verbatim consent language (recorded per-lead)">
          <textarea value={form.tcpa_language || ""} onChange={(e) => set("tcpa_language", e.target.value)}
                    rows={6}
                    data-testid="custom-lp-tcpa"
                    className="w-full border border-black px-3 py-2 text-[11px] font-mono" />
        </Field>
        <Field label="Accept leads from these states (leave empty for every state)">
          {!form.is_global_funnel && !form.is_sample_page && (form.buyer_lander_of || form.buyer_destination_id) && (
            <div className="mb-2 border border-black bg-amber-50 px-2 py-1.5 text-[11px]" data-testid="custom-lp-states-shared-note">
              These states are shared by all of this buyer&apos;s pages and their delivery settings. Saving a change here updates them all.
              Case types and other lead rules come from the buyer&apos;s settings and apply on every one of their pages.
            </div>
          )}
          {isEdit && !form.is_global_funnel && !form.is_sample_page && (form.buyer_lander_of || form.buyer_destination_id) && (
            <div className="mb-2"><NotifyBuyerBox value={notify} onChange={setNotify} testId="custom-lp-notify-buyer"
                                             preview={() => ({ kind: "states", page_id: id, states: form.state_allowlist || [] })} /></div>
          )}
          <div className="border-2 border-black bg-zinc-50 p-3" data-testid="custom-lp-state-picker">
            {/* Region + quick-action toolbar */}
            <div className="flex items-center gap-2 flex-wrap mb-3 pb-3 border-b border-zinc-300">
              <MapPin size={12} weight="bold" />
              <span className="label-mono">Quick add region:</span>
              {Object.entries(US_REGIONS).map(([label, codes]) => {
                const allSelected = codes.every((c) => (form.state_allowlist || []).includes(c));
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      const cur = new Set(form.state_allowlist || []);
                      if (allSelected) {
                        codes.forEach((c) => cur.delete(c));
                      } else {
                        codes.forEach((c) => cur.add(c));
                      }
                      set("state_allowlist", [...cur]);
                    }}
                    data-testid={`custom-lp-region-${label.toLowerCase()}`}
                    className={
                      "border border-black px-2 py-1 text-[10px] uppercase tracking-widest transition " +
                      (allSelected
                        ? "bg-black text-white"
                        : "bg-white hover:bg-black hover:text-white")
                    }
                    title={`${codes.length} states · click to ${allSelected ? "remove" : "add"} all`}
                  >
                    {label} ({codes.length}){allSelected ? " ✓" : ""}
                  </button>
                );
              })}
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => set("state_allowlist", [...US_STATES])}
                  data-testid="custom-lp-select-all-states"
                  className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest bg-white hover:bg-emerald-700 hover:border-emerald-700 hover:text-white"
                >
                  Select all 50
                </button>
                <button
                  type="button"
                  onClick={() => set("state_allowlist", [])}
                  data-testid="custom-lp-clear-states"
                  disabled={!(form.state_allowlist || []).length}
                  className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest bg-white hover:bg-red-700 hover:border-red-700 hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-black"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Checkbox grid — one row per state, 10 columns wide on desktop */}
            <div
              className="grid gap-1"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))" }}
              data-testid="custom-lp-state-grid"
            >
              {US_STATES.map((st) => {
                const active = (form.state_allowlist || []).includes(st);
                return (
                  <button
                    key={st}
                    type="button"
                    onClick={() => {
                      const cur = form.state_allowlist || [];
                      set(
                        "state_allowlist",
                        active ? cur.filter((x) => x !== st) : [...cur, st],
                      );
                    }}
                    data-testid={`custom-lp-state-${st}`}
                    aria-pressed={active}
                    className={
                      "border font-mono font-bold text-[11px] py-1.5 transition " +
                      (active
                        ? "border-black bg-black text-white shadow-[2px_2px_0_0_#f1b62c]"
                        : "border-zinc-400 bg-white hover:border-black hover:bg-amber-50")
                    }
                  >
                    {st}
                  </button>
                );
              })}
            </div>

            {/* Selection summary */}
            <div className="mt-3 pt-3 border-t border-zinc-300 flex items-center gap-2 flex-wrap">
              <span className="label-mono" data-testid="custom-lp-state-count">
                {(form.state_allowlist || []).length === 0
                  ? "Accepting all 50 states (no restriction)"
                  : `${form.state_allowlist.length} state${form.state_allowlist.length === 1 ? "" : "s"} allowed`}
              </span>
              {(form.state_allowlist || []).length > 0 && (
                <span className="text-[11px] font-mono text-zinc-600">
                  · {(form.state_allowlist || []).slice().sort().join(", ")}
                </span>
              )}
            </div>
          </div>
        </Field>
      </Section>
      )}

      {/* Iter-45: pixel overrides — always visible, including for media buyers */}
      <PixelOverridesSection
        overrides={form.pixel_overrides || { use_global_defaults: true }}
        globalPixels={globalPixels}
        onChange={(next) => set("pixel_overrides", next)}
        highlight={fixPixels}
        highlightField={fixField}
      />

      {/* Section 6 — Thank-you page (iter-44) */}
      {!isMediaBuyer && (
      <Section title="Thank-you page (what the consumer sees after submitting)">
        <Field label="Confirmation headline (leave blank for default: 'Thank you — [firm] has your case')">
          <input value={form.thankyou_headline || ""}
                 onChange={(e) => set("thankyou_headline", e.target.value)}
                 placeholder="Your firm has your case."
                 data-testid="custom-lp-thankyou-headline"
                 className="w-full border border-black px-3 py-2 text-sm font-bold" />
        </Field>
        <Field label="What happens next — message to the consumer">
          <textarea value={form.thankyou_message || ""}
                    onChange={(e) => set("thankyou_message", e.target.value)}
                    rows={4}
                    placeholder="A member of our intake team will call you within 24 hours from area code (205). Please save the number under our firm's name so you don't miss the call — and check your inbox and text messages too."
                    data-testid="custom-lp-thankyou-message"
                    className="w-full border border-black px-3 py-2 text-sm" />
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            Tip: Tell them the area code you&apos;ll call from + which channels to
            watch (helps beat spam filters + no-answer rates).
          </div>
        </Field>
        <Field label="Which channels will you contact them on?">
          <div className="flex items-center gap-3 flex-wrap" data-testid="custom-lp-thankyou-channels">
            {[
              { key: "call",  label: "📞 Phone call" },
              { key: "text",  label: "💬 Text (SMS)" },
              { key: "email", label: "✉️ Email" },
            ].map((c) => {
              const active = (form.thankyou_channels || []).includes(c.key);
              return (
                <label
                  key={c.key}
                  data-testid={`custom-lp-thankyou-channel-${c.key}`}
                  className={
                    "border-2 px-3 py-2 cursor-pointer text-sm font-bold inline-flex items-center gap-2 transition-colors duration-100 " +
                    (active
                      ? "border-emerald-800 bg-emerald-600 text-white hover:bg-emerald-700"
                      : "border-red-800 bg-red-600 text-white hover:bg-red-700")
                  }
                  data-on={active ? "1" : "0"}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => {
                      const cur = form.thankyou_channels || [];
                      set(
                        "thankyou_channels",
                        active ? cur.filter((x) => x !== c.key) : [...cur, c.key],
                      );
                    }}
                    className="hidden"
                  />
                  {c.label}
                  <span className="text-[9px] uppercase tracking-widest border border-current px-1">{active ? "On" : "Off"}</span>
                </label>
              );
            })}
          </div>
          <div className="text-[10px] text-zinc-500 mt-2 font-mono">
            Selected channels render as callout cards on the confirmation page
            so the consumer knows exactly what to expect.
          </div>
        </Field>
      </Section>
      )}

      {/* Iter-46: SEO — auto-fills from vertical defaults if blank */}
      {!isMediaBuyer && (
      <Section title="SEO — how this page shows up in Google &amp; AI search">
        <div className="flex items-center justify-end mb-2">
          <button
            type="button"
            onClick={() => setShowAiSeo(true)}
            disabled={!isEdit}
            data-testid="custom-lp-ai-seo-open"
            title={isEdit ? "Rewrite hero + SEO copy with Gemini 3.1 Pro" : "Save the page first, then use AI Rewrite"}
            className="border-2 border-black bg-violet-100 px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-violet-700 hover:text-white hover:border-violet-700 inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Sparkle size={12} weight="fill" /> AI Rewrite — make it rank harder
          </button>
        </div>
        <SeoSubsection
          firmName={form.firm_name}
          vertical={form.vertical || "other"}
          seoTitle={form.seo_title || ""}
          seoDescription={form.seo_description || ""}
          seoKeywords={form.seo_keywords || ""}
          ogImage={form.og_image || ""}
          heroImage={form.hero_image_url || ""}
          onChange={(k, v) => set(k, v)}
        />
      </Section>
      )}

      {showAiSeo && (
        <AiSeoRewriteModal
          pageId={id}
          currentValues={{
            hero_headline: form.hero_headline,
            hero_headline_accent: form.hero_headline_accent,
            hero_subheadline: form.hero_subheadline,
            seo_title: form.seo_title,
            seo_description: form.seo_description,
            seo_keywords: form.seo_keywords,
          }}
          onClose={() => setShowAiSeo(false)}
          onApply={(patch) => {
            setForm((f) => ({ ...f, ...patch }));
            setShowAiSeo(false);
          }}
        />
      )}

      {/* Iter-54: inline "create a delivery destination" modal.
          Refreshes the destinations list and drops the freshly-saved
          destination into whichever slot (primary or CC 0/1) the admin
          clicked "+ New" from. */}
      {showNewDest && (
        <NewDestinationModal
          onClose={() => setShowNewDest(false)}
          onCreated={(dest) => {
            // Refresh dropdown source-of-truth so the new row is picker-visible.
            setDestinations((prev) => [dest, ...(prev || [])]);
            if (newDestSlot === "primary") {
              set("buyer_destination_id", dest.id);
            } else {
              // "cc-0" or "cc-1"
              const idx = Number(newDestSlot.split("-")[1] || 0);
              const next = [...(form.cc_destination_ids || [])];
              next[idx] = dest.id;
              set("cc_destination_ids", next.filter(Boolean));
            }
          }}
        />
      )}

      <div className="mt-6 text-[11px] text-zinc-500 leading-relaxed">
        <Eye size={11} weight="bold" className="inline mr-1" />
        Draft &amp; paused pages are only reachable via{" "}
        <code className="font-mono bg-zinc-100 px-1">?preview=1</code>.{" "}
        {form.buyer_lander_of ? (
          <>Published, leads that fit this buyer go to them first; otherwise the next matching buyer gets them.</>
        ) : (
          <>
            Published pages route leads directly to{" "}
            <b>{destOptions.find((d) => d.id === form.buyer_destination_id)?.name || "the selected buyer"}</b>{" "}
            — bypassing state-fee routing.
          </>
        )}
      </div>
      {/* Iter-60: Mobile-only sticky action bar. Same handlers as the
          top action strip — no separate business logic. */}
      <MobileStickyActions testId="lp-editor-sticky-actions">
        <button
          onClick={() => save(false)}
          disabled={saving}
          data-testid="custom-lp-save-mobile"
          className="flex-1 border border-black bg-black text-white px-3 py-3 text-xs uppercase tracking-widest disabled:opacity-40"
        >
          {saving ? "Saving…" : (isMediaBuyer ? "Save pixels" : "Save draft")}
        </button>
        {form.slug && (
          <a
            href={pageLink(`/lp/${form.slug}${form.status !== "published" ? "?preview=1" : ""}`, form.status === "published")}
            target="_blank" rel="noreferrer"
            data-testid="custom-lp-preview-mobile"
            className="flex-1 border border-black px-3 py-3 text-xs uppercase tracking-widest text-center"
          >
            Preview
          </a>
        )}
        {!isMediaBuyer && (
          <button
            onClick={() => save(true)}
            disabled={saving}
            data-testid="custom-lp-save-publish-mobile"
            className="flex-1 border-2 border-emerald-700 bg-emerald-50 text-emerald-900 px-3 py-3 text-xs uppercase tracking-widest disabled:opacity-40"
          >
            {saving ? "…" : "Publish"}
          </button>
        )}
      </MobileStickyActions>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <ResponsiveSectionAccordion title={title} testId={testId}>
      {children}
    </ResponsiveSectionAccordion>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label-mono">
        {label}{required && <span className="text-red-700 ml-1">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


/**
 * Iter-47: AI SEO Rewriter modal.
 *
 * Preview + selective-apply UX — the admin sees the proposed rewrite
 * side-by-side with the current copy and picks which fields to accept
 * (all checked by default). Nothing is auto-applied.
 */
const AI_SEO_FIELDS = [
  { key: "hero_headline",        label: "Hero headline" },
  { key: "hero_headline_accent", label: "Hero headline accent" },
  { key: "hero_subheadline",     label: "Hero subheadline" },
  { key: "seo_title",            label: "SEO title" },
  { key: "seo_description",      label: "SEO description" },
  { key: "seo_keywords",         label: "SEO keywords" },
];

function AiSeoRewriteModal({ pageId, currentValues, onClose, onApply }) {
  const [tab, setTab] = useState("current"); // "current" | "history"
  // Iter-49.1: opening the modal must NOT auto-fire a paid Gemini call.
  // We wait for the admin to click Generate — or auto-switch to History
  // if there's a prior rewrite available (usually the intent).
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rewrite, setRewrite] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [reusedAt, setReusedAt] = useState(null);
  const [selected, setSelected] = useState(
    Object.fromEntries(AI_SEO_FIELDS.map((f) => [f.key, true])),
  );
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState(null);

  const run = React.useCallback(async () => {
    setLoading(true);
    setError("");
    setReusedAt(null);
    try {
      const r = await api.post(`/admin/custom-landing-pages/${pageId}/ai-seo-rewrite`);
      setRewrite(r.data.rewrite);
      setHistoryId(r.data.history_id || null);
      setUsage(r.data.usage);
      // Auto-refresh the history list — we just added a row.
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || "Rewrite failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  const loadHistory = React.useCallback(() => {
    api.get(`/admin/custom-landing-pages/${pageId}/ai-seo-rewrite/history`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory({ history: [], count: 0 }));
  }, [pageId]);

  const reuse = async (histId) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.post(
        `/admin/custom-landing-pages/${pageId}/ai-seo-rewrite/reuse/${histId}`,
      );
      setRewrite(r.data.rewrite);
      setHistoryId(r.data.history_id);
      setReusedAt(r.data.created_at);
      setUsage(r.data.usage);
      setTab("current");
      toast.message("Loaded from history — didn't spend a rewrite credit.");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't reuse this rewrite");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Iter-49.1: on open, ONLY load usage + history + tab-switch heuristic.
    // Never auto-fire the paid Gemini call — the admin clicks Generate.
    api.get(`/admin/custom-landing-pages/${pageId}/ai-seo-rewrite/status`)
      .then((r) => setUsage(r.data?.usage || null))
      .catch(() => {});
    loadHistory();
  }, [pageId, loadHistory]);

  // When history loads, if we have prior rewrites OR the cap is
  // exhausted, land on the History tab so the admin doesn't accidentally
  // burn a credit trying to browse past suggestions.
  useEffect(() => {
    if (!history) return;
    if (history.count > 0 && !rewrite && usage?.remaining === 0) {
      setTab("history");
    } else if (history.count > 0 && !rewrite) {
      // Soft hint — stay on Current but pre-select History via badge; the
      // admin still has to click Generate to spend a credit.
      setTab("current");
    }
  }, [history, rewrite, usage]);

  const apply = () => {
    if (!rewrite) return;
    const patch = {};
    for (const f of AI_SEO_FIELDS) {
      if (selected[f.key]) patch[f.key] = rewrite[f.key];
    }
    if (!Object.keys(patch).length) {
      toast.warning("Pick at least one field to apply.");
      return;
    }
    onApply(patch);
    toast.success(`Applied ${Object.keys(patch).length} field(s) — remember to Save.`);
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="ai-seo-rewrite-modal"
    >
      <div className="bg-white border-4 border-black max-w-5xl w-full shadow-[8px_8px_0_0_#7c3aed]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2 flex-wrap">
          <Sparkle size={16} weight="fill" className="text-violet-700" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            AI SEO Rewrite — Gemini 3.1 Pro
          </div>
          {usage && (
            <div className="ml-3 label-mono">
              {usage.used_today}/{usage.cap} today
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="ai-seo-close"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          >✕</button>
        </div>

        {/* Iter-49: tab strip — current suggestion vs history */}
        <div className="border-b-2 border-black flex" data-testid="ai-seo-tabs">
          <button
            type="button"
            onClick={() => setTab("current")}
            data-testid="ai-seo-tab-current"
            className={
              "px-5 py-3 text-[11px] uppercase tracking-widest font-bold border-r border-black " +
              (tab === "current"
                ? "bg-violet-700 text-white"
                : "bg-white hover:bg-amber-50")
            }
          >
            Current suggestion
          </button>
          <button
            type="button"
            onClick={() => setTab("history")}
            data-testid="ai-seo-tab-history"
            className={
              "px-5 py-3 text-[11px] uppercase tracking-widest font-bold border-r border-black inline-flex items-center gap-2 " +
              (tab === "history"
                ? "bg-violet-700 text-white"
                : "bg-white hover:bg-amber-50")
            }
          >
            History
            {history?.count > 0 && (
              <span className={
                "px-1.5 py-0.5 text-[9px] font-mono " +
                (tab === "history" ? "bg-white text-violet-700" : "bg-black text-white")
              }>{history.count}</span>
            )}
          </button>
        </div>

        <div className="p-6 space-y-4">
          {tab === "current" && (
            <>
              {!rewrite && !loading && !error && (
                <div className="border-2 border-dashed border-violet-400 bg-violet-50 p-6 text-center" data-testid="ai-seo-idle">
                  <Sparkle size={20} weight="fill" className="inline text-violet-700" />
                  <div className="mt-2 font-chivo font-black uppercase tracking-tighter">
                    Ready to rewrite
                  </div>
                  <div className="label-mono mt-1">
                    Clicking below spends 1 of your{" "}
                    {usage ? `${usage.remaining}/${usage.cap}` : "10/10"}{" "}
                    daily rewrite credits.{" "}
                    {history?.count > 0 && "Prior rewrites are free in the History tab."}
                  </div>
                  <button
                    onClick={run}
                    disabled={usage?.remaining === 0}
                    data-testid="ai-seo-generate"
                    className="mt-4 border-2 border-black bg-violet-700 text-white px-4 py-2 text-sm uppercase tracking-widest font-bold hover:bg-violet-800 disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    <Sparkle size={13} weight="fill" />
                    {usage?.remaining === 0 ? "Daily cap reached" : "Generate rewrite (1 credit)"}
                  </button>
                </div>
              )}

              {loading && (
                <div className="border-2 border-dashed border-violet-400 bg-violet-50 p-6 text-center" data-testid="ai-seo-loading">
                  <Sparkle size={20} weight="fill" className="inline animate-pulse text-violet-700" />
                  <div className="mt-2 label-mono">
                    Gemini is rewriting your copy… (usually 5-15s)
                  </div>
                </div>
              )}

              {error && (
                <div className="border-2 border-red-700 bg-red-50 p-3 text-sm" data-testid="ai-seo-error">
                  <b>Rewrite failed:</b> {error}
                  <div className="mt-2">
                    <button
                      onClick={run}
                      className="border border-black bg-white px-3 py-1 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {rewrite && !loading && !error && (
                <div>
                  <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed mb-4" data-testid="ai-seo-current-banner">
                    {reusedAt ? (
                      <>
                        <b>Loaded from history</b> — generated{" "}
                        <span className="font-mono">{new Date(reusedAt).toLocaleString()}</span>.{" "}
                        Tick which fields to accept then <b>Apply selected</b>.
                      </>
                    ) : (
                      <>
                        Tick which fields to accept. Nothing is saved until you
                        click <b>Apply selected</b> below and then hit{" "}
                        <b>Save</b> on the main editor.
                      </>
                    )}
                  </div>
                  <div className="space-y-3">
                    {AI_SEO_FIELDS.map((f) => (
                      <div
                        key={f.key}
                        data-testid={`ai-seo-field-${f.key}`}
                        className={
                          "border-2 p-3 " +
                          (selected[f.key]
                            ? "border-violet-700 bg-white shadow-[3px_3px_0_0_#7c3aed]"
                            : "border-zinc-400 bg-zinc-50")
                        }
                      >
                        <label className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected[f.key]}
                            onChange={() => setSelected((s) => ({ ...s, [f.key]: !s[f.key] }))}
                            data-testid={`ai-seo-toggle-${f.key}`}
                          />
                          <span className="font-chivo font-black uppercase tracking-tighter text-sm">
                            {f.label}
                          </span>
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
                          <div>
                            <div className="label-mono text-zinc-500">Current</div>
                            <div className="border border-zinc-300 bg-zinc-50 p-2 min-h-[3rem] font-mono">
                              {currentValues[f.key] || <span className="text-zinc-400">(empty)</span>}
                            </div>
                          </div>
                          <div>
                            <div className="label-mono text-violet-700">Suggested</div>
                            <div className="border border-violet-700 bg-white p-2 min-h-[3rem]">
                              {rewrite[f.key]}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Iter-49: History tab */}
          {tab === "history" && (
            <div data-testid="ai-seo-history-panel">
              <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed mb-4">
                Your last {history?.limit || 10} successful rewrites for this
                page. Click <b>Reuse</b> to load a prior suggestion back into
                the Current tab — <b>this does NOT count against your daily
                rewrite cap</b>.
              </div>
              {!history && (
                <div className="label-mono text-center py-6 text-zinc-500">Loading…</div>
              )}
              {history && history.count === 0 && (
                <div className="border border-dashed border-zinc-400 bg-zinc-50 p-6 text-center label-mono">
                  No prior rewrites yet — click Regenerate to make one.
                </div>
              )}
              {history?.history?.map((h) => (
                <div
                  key={h.id}
                  data-testid={`ai-seo-history-row-${h.id}`}
                  className="border-2 border-black bg-white p-3 mb-3"
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="font-chivo font-black uppercase tracking-tighter text-sm">
                      {new Date(h.created_at).toLocaleString()}
                    </span>
                    <span className="label-mono">
                      by {h.actor_email || "admin"} · {h.provider} · {h.model_id}
                    </span>
                    <button
                      onClick={() => reuse(h.id)}
                      disabled={loading}
                      data-testid={`ai-seo-history-reuse-${h.id}`}
                      className="ml-auto border-2 border-violet-700 bg-white text-violet-700 px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-violet-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
                    >
                      <Sparkle size={11} weight="fill" /> Reuse (free)
                    </button>
                  </div>
                  <div className="text-[12px] space-y-1">
                    <div><b>Headline:</b> <span className="font-mono">{h.rewrite?.hero_headline}</span></div>
                    <div className="text-zinc-600 line-clamp-2">
                      <b>SEO title:</b> {h.rewrite?.seo_title}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 pt-3 border-t-2 border-black">
            {tab === "current" && (
              <>
                <button
                  onClick={apply}
                  disabled={!rewrite || loading}
                  data-testid="ai-seo-apply"
                  className="border-2 border-black bg-violet-700 text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-violet-800 inline-flex items-center gap-2"
                >
                  <Sparkle size={13} weight="fill" /> Apply selected
                </button>
                {rewrite && (
                  <button
                    onClick={run}
                    disabled={loading}
                    data-testid="ai-seo-regenerate"
                    className="border-2 border-black bg-white px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
                  >
                    Regenerate
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="ml-auto border border-black px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Iter-46: SEO editor subsection.
 *
 * Every field is optional — blank falls back to per-vertical defaults on
 * the backend. Placeholders show the exact string that will be used, so
 * an admin can leave them alone or tune them for high-competition keywords.
 */
function SeoSubsection({
  firmName, vertical, seoTitle, seoDescription, seoKeywords, ogImage,
  heroImage, onChange,
}) {
  const defaults = VERTICAL_SEO_PRESETS[vertical] || VERTICAL_SEO_PRESETS.other;
  const defaultTitle = defaultSeoTitle(firmName, vertical);
  const defaultDesc = defaults.seo_description;
  const defaultKeywords = defaults.seo_keywords;
  const titleLen = seoTitle.length;
  const descLen = seoDescription.length;

  return (
    <div className="space-y-4" data-testid="custom-lp-seo-section">
      <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed">
        <div className="label-mono mb-1">How this works</div>
        Every field below is <b>optional</b> — leaving blank means the page
        auto-uses smart defaults for the <b>{verticalLabel(vertical)}</b>{" "}
        vertical. Fill only what you want to override.
      </div>

      <Field label={`SEO title (Google shows first ~60 chars) — ${titleLen}/70`}>
        <input
          value={seoTitle}
          onChange={(e) => onChange("seo_title", e.target.value)}
          placeholder={defaultTitle}
          data-testid="custom-lp-seo-title"
          maxLength={80}
          className="w-full border border-black px-3 py-2 text-sm font-bold"
        />
        <div className="text-[10px] font-mono text-zinc-500 mt-1">
          Default: <code>{defaultTitle}</code>
        </div>
      </Field>

      <Field label={`Meta description (Google shows ~155 chars) — ${descLen}/180`}>
        <textarea
          value={seoDescription}
          onChange={(e) => onChange("seo_description", e.target.value)}
          rows={3}
          maxLength={200}
          placeholder={defaultDesc}
          data-testid="custom-lp-seo-description"
          className="w-full border border-black px-3 py-2 text-sm"
        />
      </Field>

      <Field label="Keywords (comma-separated — helps AI search / LLM indexing)">
        <input
          value={seoKeywords}
          onChange={(e) => onChange("seo_keywords", e.target.value)}
          placeholder={defaultKeywords}
          data-testid="custom-lp-seo-keywords"
          className="w-full border border-black px-3 py-2 text-sm font-mono"
        />
      </Field>

      <Field label="Social share image (leave blank = uses hero image)">
        <input
          value={ogImage}
          onChange={(e) => onChange("og_image", e.target.value)}
          placeholder={heroImage || "https://…"}
          data-testid="custom-lp-og-image"
          className="w-full border border-black px-3 py-2 text-sm font-mono"
        />
        <div className="text-[10px] font-mono text-zinc-500 mt-1">
          Rec size: 1200×630 · JPG or PNG · shows on Facebook, LinkedIn,
          Twitter/X, WhatsApp, Slack, iMessage previews.
        </div>
      </Field>
    </div>
  );
}


/**
 * Iter-45.5: Vertical picker shown when creating a new custom LP. Admins
 * pick which vertical (auto, truck, rideshare, motorcycle, PI all-in-one)
 * the firm's page should be built for. Selection pre-fills the editor's
 * hero copy + stock image so the admin doesn't have to write everything
 * from scratch. Cannot be dismissed without picking (or cancelling back
 * to the list) — vertical is a required routing field.
 */
function VerticalPickerModal({ onPick, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="custom-lp-vertical-picker"
    >
      <div className="bg-white border-4 border-black max-w-3xl w-full shadow-[8px_8px_0_0_#f1b62c]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <MapPin size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            Which case type is this landing page for?
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="custom-lp-vertical-cancel"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-zinc-600 mb-5 leading-relaxed">
            Picking a vertical pre-fills the hero copy, urgency badge, stock
            image, and CTA — you can still edit anything. The vertical also
            stamps <code className="font-mono bg-zinc-100 px-1">accident_type</code> on
            every lead so buyers see the right case type in their intake.
          </p>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
            data-testid="custom-lp-vertical-grid"
          >
            {VERTICAL_ORDER.map((key) => {
              const preset = VERTICAL_PRESETS[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onPick(key)}
                  data-testid={`custom-lp-vertical-${key}`}
                  className="text-left border-2 border-black p-4 bg-white hover:bg-amber-50 hover:shadow-[4px_4px_0_0_#000] transition group"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-2 h-14 shrink-0"
                      style={{ background: preset.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-chivo font-black uppercase tracking-tighter text-base leading-tight">
                        {preset.label}
                      </div>
                      <div className="label-mono mt-0.5">{preset.tagline}</div>
                      <div className="text-[11px] text-zinc-500 mt-2 line-clamp-2">
                        &ldquo;{preset.headline}&rdquo;
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Iter-45.4: Inline AI hero generation panel (Nano Banana Pro default,
 * 10/day cap enforced server-side). Renders inside the hero-image
 * section of the editor when the "AI generate" button is toggled on.
 */
const AI_PROMPT_PRESETS = [
  { label: "Auto accident aftermath",
    prompt: "Cinematic wide shot of a two-car collision on a wet urban street at dusk, emergency lights flashing in the distance, damaged vehicles in foreground, moody documentary photography style, muted colors, no visible faces or license plates" },
  { label: "Motorcycle safety scene",
    prompt: "Dramatic dusk photo of a motorcycle helmet on the asphalt beside skid marks, empty highway stretching into the distance, warm sunset lighting, editorial photojournalism style, no visible people" },
  { label: "Truck accident scene",
    prompt: "Highway 18-wheeler pulled to the shoulder after an incident, safety flares glowing at dusk, blurred lights of passing traffic, documentary photography, moody atmospheric lighting" },
  { label: "Empathetic law-firm office",
    prompt: "Warm interior of a professional attorney's office at golden hour, focus on empty leather chairs across a mahogany desk, soft window light, framed diplomas softly out of focus in background, no visible people" },
];

function AiHeroPanel({ onGenerated, onClose }) {
  const [prompt, setPrompt] = useState(AI_PROMPT_PRESETS[0].prompt);
  const [model, setModel] = useState("pro");
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);  // {url, file_id, model}

  useEffect(() => {
    api.get("/admin/ai-hero/status")
      .then((r) => setStatus(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "AI status failed"));
  }, [preview]);

  const modelInfo = (status?.models || []).find((m) => m.key === model);
  const remaining = status?.usage?.remaining ?? 0;
  const capReached = status && remaining <= 0;

  const submit = async () => {
    if (prompt.trim().length < 8) {
      toast.error("Prompt must be at least 8 characters");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(
        "/admin/ai-hero/generate",
        { prompt, model },
      );
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const absolute = `${base}${r.data.url}`;
      setPreview({ url: absolute, file_id: r.data.file_id, model: r.data.model });
      setStatus((s) => s ? { ...s, usage: r.data.usage } : s);
      toast.success(`Generated · ${r.data.usage.remaining} left today`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const useThis = () => {
    if (!preview) return;
    onGenerated(preview.url);
    toast.success("Hero image set");
    onClose();
  };

  return (
    <div
      className="mt-3 border-2 border-black bg-white p-4"
      data-testid="ai-hero-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={14} weight="fill" />
        <div className="font-chivo font-black uppercase tracking-tighter text-sm">
          Generate hero with AI
        </div>
        <div
          className="label-mono ml-auto"
          data-testid="ai-hero-provider"
        >
          {status?.provider === "google" ? "Your Google key" : "Emergent balance"}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="border border-black w-6 h-6 flex items-center justify-center hover:bg-black hover:text-white"
          data-testid="ai-hero-close"
        >
          <X size={11} weight="bold" />
        </button>
      </div>

      <div className="mb-2 flex flex-wrap gap-1">
        {AI_PROMPT_PRESETS.map((p, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setPrompt(p.prompt)}
            data-testid={`ai-hero-preset-${i}`}
            className="text-[10px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-amber-100"
          >
            {p.label}
          </button>
        ))}
      </div>

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        maxLength={800}
        placeholder="Describe the hero image you want. Be specific — mention lighting, style, mood, no visible faces/plates for consumer compliance."
        data-testid="ai-hero-prompt"
        className="w-full border border-black px-3 py-2 text-xs font-mono mb-2"
      />
      <div className="text-[10px] font-mono text-zinc-500 mb-3">
        {prompt.length} / 800 characters
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-3">
        <div className="flex gap-1" data-testid="ai-hero-model-picker">
          {(status?.models || []).map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setModel(m.key)}
              data-testid={`ai-hero-model-${m.key}`}
              className={
                "border-2 px-3 py-1.5 text-[10px] uppercase tracking-widest transition " +
                (model === m.key
                  ? "border-black bg-black text-white"
                  : "border-zinc-400 bg-white hover:border-black")
              }
              title={`${m.label} · $${m.cost_usd.toFixed(3)} per image`}
            >
              {m.label} · ${m.cost_usd.toFixed(3)}
            </button>
          ))}
        </div>
        <div
          className="text-[11px] font-mono ml-auto"
          data-testid="ai-hero-usage"
        >
          {status ? (
            <>
              <span
                className={remaining <= 2 ? "text-amber-700 font-bold" : "text-zinc-600"}
              >
                {status.usage.used_today} / {status.usage.cap}
              </span>
              <span className="text-zinc-500"> generated today</span>
            </>
          ) : "…"}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={submit}
          disabled={generating || capReached}
          data-testid="ai-hero-generate"
          className="border-2 border-black bg-black text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Sparkle size={12} weight="fill" />
          {generating
            ? "Generating…"
            : capReached
              ? "Daily cap reached"
              : `Generate · ~$${modelInfo?.cost_usd?.toFixed(3) || "0.134"}`}
        </button>
        {preview && (
          <span className="text-[11px] font-mono text-zinc-500">
            Preview below — save uploads the image to your hero URL.
          </span>
        )}
      </div>

      {preview && (
        <div className="mt-4 border-2 border-emerald-700 p-3 bg-emerald-50" data-testid="ai-hero-preview">
          <img
            src={preview.url}
            alt="AI-generated hero"
            className="w-full h-56 object-cover border border-black mb-2"
          />
          <div className="text-[10px] font-mono text-zinc-600 mb-2">
            {preview.model?.model_id} · ~${preview.model?.cost_usd?.toFixed(3)}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={useThis}
              data-testid="ai-hero-use"
              className="border-2 border-emerald-700 bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-emerald-800"
            >
              ✓ Use this image
            </button>
            <button
              type="button"
              onClick={() => setPreview(null)}
              data-testid="ai-hero-discard"
              className="border border-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Try another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Iter-45: Per-LP pixel overrides section.
 *
 * Shows the merged (global + override) config as read-only "will fire on this
 * page" preview when `use_global_defaults=true`. Toggling the switch to
 * "per-page overrides" reveals editable inputs for each provider.
 *
 * Media buyers see ONLY this section on the editor page — every other
 * section is hidden via the parent's isMediaBuyer flag.
 */
const PIXEL_FIELDS = [
  { key: "meta_pixel_id", label: "Meta (Facebook/IG) Pixel ID", placeholder: "1234567890123456" },
  { key: "google_ads_id", label: "Google Ads ID (AW-...)", placeholder: "AW-123456789" },
  { key: "google_ads_conversion_label", label: "Google Ads conversion label", placeholder: "abcDEF123" },
  { key: "ga4_measurement_id", label: "GA4 measurement ID (G-...)", placeholder: "G-XXXXXXXX" },
  { key: "tiktok_pixel_id", label: "TikTok Pixel ID", placeholder: "CXXXXXXXXXXXXXX" },
];

function PixelOverridesSection({ overrides, globalPixels, onChange, highlight, highlightField }) {
  const useGlobal = overrides.use_global_defaults !== false;
  const setField = (k, v) => onChange({ ...overrides, [k]: v });
  const setUseGlobal = (val) =>
    onChange({ ...overrides, use_global_defaults: val });

  const merged = PIXEL_FIELDS.reduce((acc, f) => {
    const g = (globalPixels || {})[f.key];
    const o = (overrides || {})[f.key];
    acc[f.key] = useGlobal ? g : (o && o.trim ? (o.trim() || g) : (o || g));
    return acc;
  }, {});

  return (
    <div className={`brut-card mb-6 ${highlight ? HIGHLIGHT : ""}`} data-testid="custom-lp-pixels-section">
      <div className="border-b border-black px-5 py-3 font-chivo font-black uppercase tracking-tighter flex items-center gap-2">
        <Target size={14} weight="bold" /> Marketing pixels
        <span className="label-mono ml-auto normal-case tracking-normal">
          Global config lives at{" "}
          <code className="font-mono bg-zinc-100 px-1">/admin/pixels</code>
        </span>
      </div>
      <div className="p-5 space-y-4">
        {highlight && (
          <div className="border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900" data-testid="custom-lp-pixels-fix-hint">
            {highlightField && !useGlobal
              ? "Fix the highlighted pixel, then save the page."
              : "This page's pixels are here. Fix them, then save the page. To re-test, run the live test on Marketing Pixels."}
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          {[
            { key: true,  label: "Use global defaults" },
            { key: false, label: "Set per-page overrides" },
          ].map((opt) => (
            <label
              key={String(opt.key)}
              data-testid={`custom-lp-pixels-mode-${opt.key ? "global" : "override"}`}
              className={
                "border-2 px-3 py-2 cursor-pointer text-sm font-bold inline-flex items-center gap-2 transition " +
                (useGlobal === opt.key
                  ? "border-black bg-black text-white shadow-[2px_2px_0_0_#f1b62c]"
                  : "border-zinc-400 bg-white hover:border-black hover:bg-amber-50")
              }
            >
              <input
                type="radio"
                name="pixel-mode"
                checked={useGlobal === opt.key}
                onChange={() => setUseGlobal(opt.key)}
                className="hidden"
              />
              {opt.label}
            </label>
          ))}
        </div>

        {useGlobal ? (
          <div className="border border-black bg-zinc-50 p-4" data-testid="custom-lp-pixels-preview">
            <div className="label-mono mb-2">Firing on this page (from global config)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {PIXEL_FIELDS.map((f) => {
                const val = merged[f.key];
                return (
                  <div key={f.key} className="flex items-center justify-between border border-zinc-300 bg-white px-2 py-1.5">
                    <span className="font-mono text-[11px] text-zinc-500">{f.label}</span>
                    {val ? (
                      <span className="font-mono font-bold text-[11px] text-emerald-700">✓ {val}</span>
                    ) : (
                      <span className="font-mono text-[11px] text-zinc-400">not set</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {PIXEL_FIELDS.map((f) => {
              const g = (globalPixels || {})[f.key];
              return (
                <div key={f.key}>
                  <label className="label-mono">
                    {f.label}
                    {g && (
                      <span className="ml-2 normal-case tracking-normal text-[10px] text-zinc-500">
                        (global: <code className="font-mono">{g}</code>)
                      </span>
                    )}
                  </label>
                  <input
                    value={overrides[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    data-testid={`custom-lp-pixel-${f.key}`}
                    data-highlighted={f.key === highlightField ? "true" : undefined}
                    className={`w-full border border-black px-3 py-2 mt-1 text-sm font-mono ${f.key === highlightField ? HIGHLIGHT : ""}`}
                  />
                </div>
              );
            })}
            <div className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-300">
              Blank fields fall back to the global config above. Fill only
              what this firm wants to override.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Iter-51: Launch-Readiness panel for the custom landing-page editor.
 *
 * Renders the aggregate status pill (READY / NOT READY), the
 * platform-config snapshot (bot defense + Turnstile + TrustedForm —
 * booleans only, never any secret), and a grouped list of findings by
 * category with per-item severity badges.
 *
 * Never mutates page config. Silent-fails on 404 (unsaved page).
 */
function LaunchReadinessPanel({ readiness, loading, onRefresh, isPublished, saving, onSave, onQuickFix, pageId, isBuyerPage }) {
  const [collapsed, setCollapsed] = React.useState(false);

  if (loading && !readiness) {
    return (
      <div className="brut-card mb-6" data-testid="launch-readiness-panel-loading">
        <div className="px-4 py-3 text-[11px] font-mono">Loading launch readiness…</div>
      </div>
    );
  }
  if (!readiness) {
    return (
      <div className="brut-card mb-6" data-testid="launch-readiness-panel-empty">
        <div className="px-4 py-3 text-[11px] font-mono text-zinc-500">
          Save the page once to run the launch-readiness check.
        </div>
      </div>
    );
  }

  const ready = readiness.ready;
  const bc = readiness.blocker_count || 0;
  const wc = readiness.warning_count || 0;
  const ic = readiness.info_count || 0;

  // Group findings by category so ops can walk them section-by-section.
  const grouped = {};
  for (const f of readiness.findings || []) {
    (grouped[f.category] = grouped[f.category] || []).push(f);
  }
  const CATEGORY_LABELS = {
    BUYER_ROUTING: "Buyer & routing",
    LANDING_PAGE: "Landing page",
    COMPLIANCE: "Compliance",
    ANTI_BOT: "Anti-bot / traffic quality",
    PIXELS: "Marketing pixels",
    FINANCIAL: "Financial / ops",
    TESTING: "Testing",
  };

  const severityStyle = (sev) => ({
    BLOCKER: "border-red-700 bg-red-50 text-red-900",
    WARNING: "border-amber-700 bg-amber-50 text-amber-900",
    INFO: "border-zinc-500 bg-zinc-50 text-zinc-700",
  }[sev] || "border-black bg-white");

  const severityIcon = (sev) => {
    if (sev === "BLOCKER") return <XCircle size={13} weight="fill" className="text-red-700" />;
    if (sev === "WARNING") return <WarningCircle size={13} weight="fill" className="text-amber-700" />;
    return <Info size={13} weight="fill" className="text-zinc-500" />;
  };

  return (
    <div
      className="brut-card mb-6"
      data-testid="launch-readiness-panel"
      style={{
        borderColor: ready ? "#047857" : bc > 0 ? "#b91c1c" : "#b45309",
        borderWidth: 2,
      }}
    >
      <div className="border-b border-black px-4 py-3 flex items-center gap-2 flex-wrap">
        {ready
          ? <CheckCircle size={16} weight="fill" className="text-emerald-700" />
          : <WarningCircle size={16} weight="fill" className={bc > 0 ? "text-red-700" : "text-amber-700"} />}
        <div className="font-chivo font-black uppercase tracking-tighter">
          Launch readiness
        </div>
        <div
          data-testid="launch-readiness-status"
          className={
            "text-[10px] uppercase tracking-widest px-2 py-0.5 border font-bold " +
            (ready
              ? "bg-emerald-700 border-emerald-700 text-white"
              : bc > 0
                ? "bg-red-700 border-red-700 text-white"
                : "bg-amber-500 border-amber-700 text-white")
          }
        >
          {ready
            ? "Ready for traffic"
            : `Not ready — ${bc + wc} item${bc + wc === 1 ? "" : "s"} need attention`}
        </div>
        <div className="label-mono ml-2 flex gap-3">
          {bc > 0 && <span className="text-red-700">{bc} blocker{bc === 1 ? "" : "s"}</span>}
          {wc > 0 && <span className="text-amber-700">{wc} warning{wc === 1 ? "" : "s"}</span>}
          {ic > 0 && <span className="text-zinc-500">{ic} info</span>}
        </div>
        {isPublished && bc > 0 && (
          <div className="text-[10px] font-mono text-emerald-700 border border-emerald-700 px-1.5 py-0.5 uppercase tracking-widest">
            Live page — re-publish stays allowed
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="launch-readiness-save"
            className="border border-black bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save & re-check"}
          </button>
          <button
            onClick={onRefresh}
            data-testid="launch-readiness-refresh"
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            {loading ? "…" : "Refresh"}
          </button>
          {isBuyerPage && pageId && (
            <DomainCheckButton body={{ page_id: pageId }} testId="launch-readiness-domain-check"
              className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white disabled:opacity-40" />
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            {collapsed ? "Show" : "Hide"} details
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Platform snapshot — booleans only, never secrets. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px] font-mono">
            <PlatformDot
              label="Bot defense"
              on={readiness.platform?.bot_defense_enabled}
              testId="readiness-platform-bot"
            />
            <PlatformDot
              label="Cloudflare Turnstile"
              on={readiness.platform?.turnstile_configured}
              testId="readiness-platform-turnstile"
            />
            <PlatformDot
              label="TrustedForm"
              on={readiness.platform?.trustedform_configured}
              testId="readiness-platform-trustedform"
            />
          </div>

          {/* Findings grouped by category */}
          {Object.keys(CATEGORY_LABELS).map((cat) => {
            const items = grouped[cat] || [];
            if (!items.length) return null;
            return (
              <div key={cat}>
                <div className="label-mono mb-2">{CATEGORY_LABELS[cat]}</div>
                <div className="border border-black divide-y divide-zinc-200">
                  {items.map((f, i) => (
                    <div
                      key={f.code + i}
                      data-testid={`readiness-finding-${f.code}`}
                      className={"p-2 flex gap-2 items-start " + severityStyle(f.severity)}
                    >
                      <div className="mt-0.5 shrink-0">{severityIcon(f.severity)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-bold">{f.message}</div>
                        {f.fix_hint && (
                          <div className="text-[10px] mt-0.5 opacity-80">{f.fix_hint}</div>
                        )}
                        <FindingFix f={f} onQuickFix={onQuickFix} busy={saving} />
                      </div>
                      <span className="text-[9px] uppercase tracking-widest border border-current px-1 py-0.5 font-bold">
                        {f.severity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlatformDot({ label, on, testId }) {
  return (
    <div className="flex items-center gap-2 border border-black px-2 py-1.5" data-testid={testId}>
      <span
        style={{
          display: "inline-block", width: 8, height: 8, borderRadius: 999,
          background: on ? "#047857" : "#b45309",
        }}
      />
      <span className="font-bold uppercase tracking-widest text-[10px]">{label}</span>
      <span className={"ml-auto text-[10px] " + (on ? "text-emerald-700" : "text-amber-700")}>
        {on ? "configured" : "not configured"}
      </span>
    </div>
  );
}

